import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { trackEvent } from '../../utils/analytics';
import { generateFAQSchema, JsonLd } from '../../utils/structuredData';
import { getPublicToolsCatalog } from '../../services/publicToolsApi';
import { useInMarketingShell } from '../Marketing/MarketingContext';
import AIEFaq from '../AIEmployees/sections/AIEFaq';
import AIEClose from '../AIEmployees/sections/AIEClose';
import {
  APP_NAMES,
  TOOLS_CATEGORIES,
  TOOLS_CLOSE,
  TOOLS_DIRECTORY_HEAD,
  TOOLS_FAQ_HEAD,
  TOOLS_FAQ_ITEMS,
  TOOLS_FEATURED,
  TOOLS_FEATURED_HEAD,
  TOOLS_FEATURED_SLUGS,
  TOOLS_HERO,
  TOOLS_JOBS,
  TOOLS_JOBS_HEAD,
  TOOLS_LEGEND_NOTE,
  TOOLS_LEVELS,
  TOOLS_PAGE_PATH,
  TOOLS_SEO,
  TOOLS_WAYS,
  logoFor,
} from '../../content/toolsPage';
import { countsById, groupByCategory, previewFor, searchApps } from './toolsDirectory';
import '../AIEmployees/AIEmployeesLandingPage.css';
import './ToolsPage.css';

/**
 * ToolsPage: the public cellcog.ai/tools page (Nitish + Maeve, Nov 3 2026; spec agent_docs/tools_page/REQUIREMENTS.md, mock v3).
 *
 * Sections: hero (search + 12 logos) · jobs (nine tabs, stacked panels of one height, one legend) · featured (36 apps, not
 * links) · the full list (hydrates from GET /api/cellcog/public/tools/catalog) · three ways to connect · FAQ (the AIEFaq
 * accordion, FAQPage JSON-LD from the same list) · close. Copy lives in content/toolsPage.js.
 *
 * Crawlers: everything except the full list renders from the content file, so react-snap bakes it into the HTML and the .md
 * alternate is generated from that HTML (scripts/marketingHtmlTwins.js). The list is never fetched during the prerender.
 *
 * Theme: the .aie-landing token system (the shell pins .dark; MarketingAdaptiveTheme.css remaps the light face). Every
 * third-party logo sits on a LIGHT plate on both faces (the Connectors --connectors-logo-plate rule).
 *
 * Tracking (TRACKING.md "Page: /tools"): section_view {section: tools_hero|tools_jobs|tools_featured|tools_directory|
 * tools_ways|tools_faq, page}; app_click via data-track (tools_job_tab, tools_category_filter, tools_show_all,
 * tools_show_less, tools_faq_open, each with data-track-detail); tools_search as app_click {detail: hero|directory, state:
 * hit|none}, once per settled search, never the typed text; cta_click {cta_name: get_started, location: tools_cta}.
 * Registrations: App.js route, adaptivePaths.js, seo.py STATIC_PAGES, react-snap include, marketingHtmlTwins.js (.md),
 * robots.txt, llms.txt, public/index.html PostHog route list, MarketingFooter; LB URL map (Arjun).
 */

const SEARCH_SETTLE_MS = 900;

const isPrerender = () => typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent);
// Search and AI crawlers: if the list fails for one of them, the page keeps the neutral loading row, so no index ever stores
// the failure line (robots.txt also allows the catalog endpoint, so Google's renderer can normally load it).
const CRAWLER_UA = /Googlebot|Google-InspectionTool|AdsBot-Google|Storebot-Google|bingbot|DuckDuckBot|YandexBot|Baiduspider|Applebot|GPTBot|OAI-SearchBot|ChatGPT-User|ClaudeBot|Claude-User|Claude-SearchBot|PerplexityBot|Perplexity-User|facebookexternalhit|Twitterbot|LinkedInBot|Slackbot/i;
export const isCrawler = () => typeof navigator !== 'undefined' && CRAWLER_UA.test(navigator.userAgent);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

/** A catalog logo on the light plate. A logo that fails shows the app's first letter instead of an empty plate, EXCEPT during
 *  the react-snap prerender: the crawl aborts every third-party request (skipThirdPartyRequests), so an error there says
 *  nothing about the logo, and the snapshot must keep the <img> for the visitor's browser to load. */
const Plate = ({ slug, name, src, className = '', eager = false }) => {
  const [failed, setFailed] = useState(false);
  const onError = () => { if (!isPrerender()) setFailed(true); };
  return (
    <span className={`tools-plate ${className}`}>
      {failed ? (
        <span className="tools-plate__letter" aria-hidden="true">{(name || slug || '?').charAt(0)}</span>
      ) : (
        <img src={src || logoFor(slug)} alt="" loading={eager ? 'eager' : 'lazy'} onError={onError} />
      )}
    </span>
  );
};

const SectionHead = ({ kicker, title, sub, id }) => (
  <div className="tools-head">
    <div className="tools-kicker">{kicker}</div>
    <h2 id={id} className="tools-h2 aie-display">{title}</h2>
    {sub && <p className="tools-sub">{sub}</p>}
  </div>
);

const SearchBox = ({ value, onChange, onEnter, placeholder, size }) => (
  <div className={`tools-search tools-search--${size}`}>
    <SearchIcon />
    <input
      type="search"
      value={value}
      placeholder={placeholder}
      aria-label={placeholder}
      autoComplete="off"
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) onEnter(); }}
    />
  </div>
);

// ------------------------------------------------------------------------------------------------------------- hero
const Hero = ({ query, onQuery, onEnter }) => (
  <div className="tools-hero">
    <span className="tools-eyebrow"><b>{TOOLS_HERO.eyebrowCount}</b> {TOOLS_HERO.eyebrowRest}</span>
    <h1 className="tools-h1 aie-display">{TOOLS_HERO.title}</h1>
    <p className="tools-hero__sub">{TOOLS_HERO.sub}</p>
    <SearchBox value={query} onChange={onQuery} onEnter={onEnter} placeholder={TOOLS_HERO.searchPlaceholder} size="hero" />
    <div className="tools-strip">
      {TOOLS_HERO.strip.map((slug) => (
        <span key={slug} title={APP_NAMES[slug]} className="tools-strip__item">
          <Plate slug={slug} name={APP_NAMES[slug]} eager />
          <span className="tools-sr">{APP_NAMES[slug]}</span>
        </span>
      ))}
    </div>
  </div>
);

// ------------------------------------------------------------------------------------------------------------- jobs
const Jobs = () => {
  const [job, setJob] = useState(TOOLS_CATEGORIES[0].id);
  return (
    <div className="tools-wrap">
      <SectionHead kicker={TOOLS_JOBS_HEAD.kicker} title={TOOLS_JOBS_HEAD.title} sub={TOOLS_JOBS_HEAD.sub} />
      <div className="tools-jobs">
        <div className="tools-jobs__tabs" role="tablist" aria-label={TOOLS_JOBS_HEAD.kicker}>
          {TOOLS_CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              id={`tools-job-tab-${c.id}`}
              aria-selected={job === c.id}
              aria-controls={`tools-job-${c.id}`}
              className={`tools-jobs__tab${job === c.id ? ' is-on' : ''}`}
              data-track="tools_job_tab"
              data-track-detail={c.id}
              onClick={() => setJob(c.id)}
            >
              <span className="tools-jobs__dot" aria-hidden="true" />
              {c.label}
            </button>
          ))}
        </div>
        {/* ONE box: all nine panels stacked in one grid cell (the box takes the tallest panel's height, so a tab switch
            never jumps; inactive panels are visibility:hidden, not unmounted), then the legend once at the bottom. */}
        <div className="tools-jobs__box">
          <div className="tools-jobs__stack">
            {TOOLS_CATEGORIES.map((c) => {
              const j = TOOLS_JOBS[c.id];
              return (
                <div
                  key={c.id}
                  id={`tools-job-${c.id}`}
                  role="tabpanel"
                  aria-labelledby={`tools-job-tab-${c.id}`}
                  className={`tools-job${job === c.id ? ' is-on' : ''}`}
                  data-job={c.id}
                >
                  <h3 className="tools-job__title">{c.label}</h3>
                  <ul className="tools-job__apps">
                    {j.apps.map((slug) => (
                      <li key={slug} className="tools-job__app">
                        <Plate slug={slug} name={APP_NAMES[slug]} />
                        {APP_NAMES[slug]}
                      </li>
                    ))}
                  </ul>
                  <div className="tools-job__ask">
                    <small>{TOOLS_JOBS_HEAD.askLabel}</small>
                    <p>&quot;{j.ask}&quot;</p>
                  </div>
                  <ol className="tools-job__steps">
                    {j.steps.map(([lv, text]) => (
                      <li key={text} className={`tools-lv tools-lv--${lv}`} data-level={lv}>
                        <span className="tools-sr">{TOOLS_LEVELS[lv].word}: </span>
                        {text}
                      </li>
                    ))}
                  </ol>
                </div>
              );
            })}
          </div>
          {/* The {' '} between items: a flex row drops whitespace-only text visually, but screen readers and the .md need it. */}
          <p className="tools-legend">
            {['s', 'm', 'd'].map((lv) => (
              <React.Fragment key={lv}>
                <span className={`tools-lv tools-lv--${lv}`}>
                  <b>{TOOLS_LEVELS[lv].word}</b> {TOOLS_LEVELS[lv].meaning}
                </span>{' '}
              </React.Fragment>
            ))}
            <span className="tools-legend__note">{TOOLS_LEGEND_NOTE}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------------------------------------- featured
const Featured = () => (
  <div className="tools-wrap">
    <SectionHead kicker={TOOLS_FEATURED_HEAD.kicker} title={TOOLS_FEATURED_HEAD.title} sub={TOOLS_FEATURED_HEAD.sub} />
    <ul className="tools-feat">
      {TOOLS_FEATURED.map((f) => (
        <li key={f.slug} className="tools-feat__card" data-slug={f.slug}>
          <Plate slug={f.slug} name={f.name} />
          <div>
            <b className="tools-feat__name">{f.name}</b>
            <span className="tools-feat__line">{f.line}</span>
          </div>
        </li>
      ))}
    </ul>
  </div>
);

// -------------------------------------------------------------------------------------------------------- directory
const Tile = ({ app }) => (
  <li className="tools-tile" data-slug={app.slug}>
    <Plate slug={app.slug} name={app.name} src={app.logo_url} />
    <span className="tools-tile__name">{app.name}</span>
  </li>
);

const Directory = ({ catalog, status, query, onQuery }) => {
  const [cat, setCat] = useState('all');
  const [open, setOpen] = useState({});
  const grouped = useMemo(() => groupByCategory(catalog), [catalog]);
  const counts = useMemo(() => countsById(catalog), [catalog]);
  const hits = useMemo(() => searchApps(catalog, query), [catalog, query]);
  const q = query.trim();

  const showLess = (id) => {
    setOpen((o) => ({ ...o, [id]: false }));
    // Back to the category's heading (its scroll-margin-top clears the sticky header), after the list collapses.
    window.requestAnimationFrame(() => {
      const el = document.getElementById(`tools-cat-${id}`);
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start' });
    });
  };

  let body;
  if (status === 'error') {
    body = <p className="tools-note">{TOOLS_DIRECTORY_HEAD.unavailable}</p>;
  } else if (status !== 'ready') {
    body = (
      <div className="tools-tiles tools-tiles--loading" aria-hidden="true">
        {Array.from({ length: 8 }).map((_, i) => <span key={i} className="tools-tile tools-tile--ghost" />)}
      </div>
    );
  } else if (q) {
    body = hits.length ? (
      <div className="tools-cat">
        <div className="tools-cat__head">
          <h3>{TOOLS_DIRECTORY_HEAD.resultsLabel}</h3>
          <em>{hits.length}</em>
        </div>
        <ul className="tools-tiles">{hits.map((a) => <Tile key={a.slug} app={a} />)}</ul>
      </div>
    ) : (
      <p className="tools-note">
        {TOOLS_DIRECTORY_HEAD.noResult(q)} <a href="#tools-ways">{TOOLS_DIRECTORY_HEAD.noResultLink}</a>
      </p>
    );
  } else {
    body = TOOLS_CATEGORIES.filter((c) => cat === 'all' || cat === c.id).map((c) => {
      const all = grouped[c.id] || [];
      const count = counts[c.id] ?? all.length;
      const full = cat === c.id || open[c.id];
      const preview = previewFor(all, TOOLS_FEATURED_SLUGS);
      // Never a heading over an empty grid: no apps, or (in the overview) every app is featured above. The category's pill
      // still opens it in full (the in-app visibleCategories rule).
      if (!all.length || (!full && !preview.length)) return null;
      const shown = full ? all : preview;
      // "Show all" only when the preview hides something; "Show less" while open.
      const toggle = cat !== c.id && (open[c.id] || all.length > preview.length);
      return (
        <div key={c.id} className="tools-cat" id={`tools-cat-${c.id}`} data-cat={c.id}>
          <div className="tools-cat__head">
            <h3>{c.label}</h3>
            <em>{count}</em>
            <span className="tools-cat__sp" />
            {toggle && (open[c.id] ? (
              <button type="button" className="tools-cat__more" data-track="tools_show_less" data-track-detail={c.id} onClick={() => showLess(c.id)}>
                Show less ↑
              </button>
            ) : (
              <button
                type="button"
                className="tools-cat__more"
                data-track="tools_show_all"
                data-track-detail={c.id}
                onClick={() => setOpen((o) => ({ ...o, [c.id]: true }))}
              >
                Show all {count} →
              </button>
            ))}
          </div>
          <ul className="tools-tiles">{shown.map((a) => <Tile key={a.slug} app={a} />)}</ul>
        </div>
      );
    });
  }

  return (
    <div className="tools-wrap">
      <SectionHead kicker={TOOLS_DIRECTORY_HEAD.kicker} title={TOOLS_DIRECTORY_HEAD.title} sub={TOOLS_DIRECTORY_HEAD.sub} />
      <div className="tools-dirtop">
        <SearchBox value={query} onChange={onQuery} placeholder={TOOLS_DIRECTORY_HEAD.searchPlaceholder} size="dir" />
        <div className="tools-cats" role="group" aria-label="Categories">
          {[{ id: 'all', label: TOOLS_DIRECTORY_HEAD.allLabel }, ...TOOLS_CATEGORIES].map((c) => (
            <button
              key={c.id}
              type="button"
              aria-pressed={cat === c.id}
              className={cat === c.id ? 'is-on' : ''}
              data-track="tools_category_filter"
              data-track-detail={c.id}
              onClick={() => setCat(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div className="tools-dir">{body}</div>
    </div>
  );
};

// ------------------------------------------------------------------------------------------------------------- ways
const WayVisual = ({ id }) => {
  if (id === 'app') {
    return (
      <div className="tools-way__vis" aria-hidden="true">
        <div className="tools-vrow"><Plate slug="gmail" name="Gmail" /><span>Gmail</span><em className="tools-vbtn is-ok">Connected</em></div>
        <div className="tools-vrow"><Plate slug="hubspot" name="HubSpot" /><span>HubSpot</span><em className="tools-vbtn">Connect</em></div>
      </div>
    );
  }
  const fields = id === 'mcp'
    ? [['Server URL', 'https://mcp.yourcompany.com'], ['Sign in', 'OAuth or API key']]
    : [['Name', 'SHIPSTATION_API_KEY'], ['Value', '••••••••••••••••••••']];
  return (
    <div className="tools-way__vis" aria-hidden="true">
      {fields.map(([label, value]) => (
        <div key={label} className="tools-field"><span>{label}</span><div>{value}</div></div>
      ))}
    </div>
  );
};

const Ways = () => (
  <div className="tools-wrap">
    <SectionHead kicker={TOOLS_WAYS.kicker} title={TOOLS_WAYS.title} sub={TOOLS_WAYS.sub} />
    <div className="tools-ways">
      {TOOLS_WAYS.cards.map((w) => (
        <div key={w.id} className="tools-way">
          <WayVisual id={w.id} />
          <h3>{w.title}</h3>
          <p>{w.body}</p>
        </div>
      ))}
    </div>
  </div>
);

// ------------------------------------------------------------------------------------------------------------- page
const ToolsPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { currentUser } = useAuth();
  const inMarketingShell = useInMarketingShell();
  const dark = inMarketingShell ? true : isDarkMode;
  const rootRef = useRef(null);
  const canonicalUrl = `${SEO_DEFAULTS.url}${TOOLS_PAGE_PATH}`;

  const [catalog, setCatalog] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [query, setQuery] = useState('');
  const [searchFrom, setSearchFrom] = useState('hero');
  const lastSearch = useRef('');

  // The full list: never fetched during the react-snap prerender (no backend at build time; the HTML keeps the loading row).
  useEffect(() => {
    if (isPrerender()) return undefined;
    let live = true;
    getPublicToolsCatalog()
      .then((res) => {
        if (!live) return;
        if (res && res.data && Array.isArray(res.data.apps)) {
          setCatalog(res.data);
          setStatus('ready');
        } else if (!isCrawler()) {
          setStatus('error');
        }
      })
      .catch(() => live && !isCrawler() && setStatus('error'));
    return () => { live = false; };
  }, []);

  // tools_search: once per settled search, from the box the visitor typed in, hit or none. Never the typed text.
  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) lastSearch.current = ''; // a cleared box: typing the same words again is a new search
    if (!q || status !== 'ready') return undefined;
    const t = setTimeout(() => {
      const key = `${searchFrom}:${q}`;
      if (key === lastSearch.current) return;
      lastSearch.current = key;
      trackEvent('app_click', {
        element: 'tools_search',
        surface: 'tools',
        detail: searchFrom,
        state: searchApps(catalog, q).length ? 'hit' : 'none',
        page_path: TOOLS_PAGE_PATH,
      });
    }, SEARCH_SETTLE_MS);
    return () => clearTimeout(t);
  }, [query, searchFrom, status, catalog]);

  // section_view: one event per section, the first time it is meaningfully on screen (the AI Employees page observer).
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const root = rootRef.current;
    if (!root) return undefined;
    const seen = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        const vh = (en.rootBounds && en.rootBounds.height) || window.innerHeight;
        if (en.intersectionRatio < 0.2 && en.intersectionRect.height < vh * 0.5) return;
        const label = en.target.getAttribute('data-tools-section');
        if (seen.has(label)) return;
        seen.add(label);
        trackEvent('section_view', { section: label, page: TOOLS_PAGE_PATH });
        io.unobserve(en.target);
      });
    }, { threshold: [0.05, 0.1, 0.15, 0.2] });
    root.querySelectorAll('[data-tools-section]').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const onHeroQuery = (v) => { setSearchFrom('hero'); setQuery(v); };
  const onDirQuery = (v) => { setSearchFrom('directory'); setQuery(v); };
  const toDirectory = () => {
    const el = document.getElementById('tools-directory');
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onGetStarted = () => {
    trackEvent('cta_click', { cta_name: 'get_started', page: TOOLS_PAGE_PATH, location: 'tools_cta' });
    navigate(currentUser ? '/connectors' : '/signup');
  };

  return (
    <>
      <Helmet>
        <title>{TOOLS_SEO.title}</title>
        <meta name="description" content={TOOLS_SEO.description} />
        <link rel="canonical" href={canonicalUrl} />
        {/* .md alternate: generated from this page's prerendered HTML at postbuild (scripts/marketingHtmlTwins.js). */}
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={TOOLS_SEO.title} />
        <meta property="og:description" content={TOOLS_SEO.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${SEO_DEFAULTS.url}/assets/tools-og.png`} />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={TOOLS_SEO.title} />
        <meta name="twitter:description" content={TOOLS_SEO.description} />
        <meta name="twitter:image" content={`${SEO_DEFAULTS.url}/assets/tools-og.png`} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: TOOLS_SEO.title,
          description: TOOLS_SEO.description,
          url: canonicalUrl,
          isPartOf: { '@type': 'WebSite', name: SEO_DEFAULTS.siteName, url: SEO_DEFAULTS.url },
          dateModified: '2026-11-03',
        }}
      />
      <JsonLd data={generateFAQSchema(TOOLS_FAQ_ITEMS)} />

      <div className={`aie-landing tools-page ${dark ? 'dark' : 'light'}`} ref={rootRef} data-track-surface="tools">
        <section data-tools-section="tools_hero">
          <Hero query={query} onQuery={onHeroQuery} onEnter={toDirectory} />
        </section>
        <section className="tools-band tools-band--alt" data-tools-section="tools_jobs">
          <Jobs />
        </section>
        <section className="tools-band" data-tools-section="tools_featured">
          <Featured />
        </section>
        <section className="tools-band tools-band--alt" id="tools-directory" data-tools-section="tools_directory">
          <Directory catalog={catalog} status={status} query={query} onQuery={onDirQuery} />
        </section>
        <section className="tools-band" id="tools-ways" data-tools-section="tools_ways">
          <Ways />
        </section>
        <div className="tools-band--alt tools-faq" data-tools-section="tools_faq">
          <AIEFaq
            items={TOOLS_FAQ_ITEMS}
            eyebrow={TOOLS_FAQ_HEAD.eyebrow}
            title={TOOLS_FAQ_HEAD.title}
            sub={null}
            titleId="tools-faq-title"
            trackOpen="tools_faq_open"
          />
        </div>
        <AIEClose ctaLabel={TOOLS_CLOSE.cta} onPrimary={onGetStarted} title={TOOLS_CLOSE.title} sub={TOOLS_CLOSE.sub} />
      </div>
    </>
  );
};

export default ToolsPage;
